/**
 * Persist idle-aware CRM dwell sessions for the team activity report.
 */
import {
  addDoc,
  collection,
  getDocs,
  query,
  Timestamp,
  where,
} from 'firebase/firestore';
import type { CrmActivityEntityType, CrmActivitySession } from '../types';
import { db } from './firebase';
import {
  enqueueCrmActivitySessionWrite,
  registerCrmActivitySessionWriter,
} from './crmActivitySessionWriteQueue';
import { bumpCrmTrackerHealth } from './crmTrackerHealth';

export const CRM_ACTIVITY_SESSIONS = 'crmActivitySessions';

registerCrmActivitySessionWriter(async (session) => {
  const id = await saveCrmActivitySessionDirect(session);
  if (id) await bumpCrmTrackerHealth(session.userId, 'retried');
  return id;
});

function toDate(value: unknown): Date {
  if (value instanceof Date) return value;
  if (value && typeof (value as { toDate?: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  if (typeof value === 'string' || typeof value === 'number') return new Date(value);
  return new Date();
}

function toTimestamp(value: Date | Timestamp): Timestamp {
  return value instanceof Date ? Timestamp.fromDate(value) : value;
}

export function toFirestoreCrmActivitySession(
  session: Omit<CrmActivitySession, 'id'>,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    userId: session.userId,
    userName: session.userName,
    userEmail: session.userEmail ?? null,
    type: session.type ?? 'implied',
    minutes: session.minutes,
    startedAt: toTimestamp(session.startedAt),
    endedAt: toTimestamp(session.endedAt),
    path: session.path,
    pageKey: session.pageKey,
    pageLabel: session.pageLabel,
    activityClass: session.activityClass ?? null,
    jobId: session.jobId ?? null,
    dealId: session.dealId ?? null,
    entityType: session.entityType ?? null,
    entityId: session.entityId ?? null,
    entityName: session.entityName ?? null,
    createdAt: toTimestamp(session.createdAt),
  };
  if (typeof session.presenceMinutes === 'number') payload.presenceMinutes = session.presenceMinutes;
  if (typeof session.activeSeconds === 'number') payload.activeSeconds = session.activeSeconds;
  if (typeof session.presenceSeconds === 'number') payload.presenceSeconds = session.presenceSeconds;
  if (session.moduleKey) payload.moduleKey = session.moduleKey;
  if (session.moduleLabel) payload.moduleLabel = session.moduleLabel;
  if (session.viewKey) payload.viewKey = session.viewKey;
  if (session.viewLabel) payload.viewLabel = session.viewLabel;
  if (session.search != null) payload.search = session.search;
  if (session.tabId) payload.tabId = session.tabId;
  if (session.sessionId) payload.sessionId = session.sessionId;
  if (session.attributedTo) payload.attributedTo = session.attributedTo;
  if (session.projectId) payload.projectId = session.projectId;
  if (session.leadId) payload.leadId = session.leadId;
  if (typeof session.focused === 'boolean') payload.focused = session.focused;
  if (typeof session.visible === 'boolean') payload.visible = session.visible;
  return payload;
}

export function fromFirestoreCrmActivitySession(
  id: string,
  data: Record<string, unknown>,
): CrmActivitySession {
  return {
    id,
    userId: typeof data.userId === 'string' ? data.userId : '',
    userName: typeof data.userName === 'string' ? data.userName : 'Unknown',
    userEmail: typeof data.userEmail === 'string' ? data.userEmail : undefined,
    type: 'implied',
    minutes: typeof data.minutes === 'number' ? data.minutes : 0,
    presenceMinutes: typeof data.presenceMinutes === 'number' ? data.presenceMinutes : undefined,
    activeSeconds: typeof data.activeSeconds === 'number' ? data.activeSeconds : undefined,
    presenceSeconds: typeof data.presenceSeconds === 'number' ? data.presenceSeconds : undefined,
    startedAt: toDate(data.startedAt),
    endedAt: toDate(data.endedAt),
    path: typeof data.path === 'string' ? data.path : '/',
    pageKey: typeof data.pageKey === 'string' ? data.pageKey : 'other',
    pageLabel: typeof data.pageLabel === 'string' ? data.pageLabel : 'CRM',
    activityClass: (data.activityClass as CrmActivitySession['activityClass']) || undefined,
    jobId: typeof data.jobId === 'string' ? data.jobId : undefined,
    dealId: typeof data.dealId === 'string' ? data.dealId : undefined,
    entityType: (data.entityType as CrmActivityEntityType | undefined) || undefined,
    entityId: typeof data.entityId === 'string' ? data.entityId : undefined,
    entityName: typeof data.entityName === 'string' ? data.entityName : undefined,
    createdAt: toDate(data.createdAt),
    moduleKey: typeof data.moduleKey === 'string' ? data.moduleKey : undefined,
    moduleLabel: typeof data.moduleLabel === 'string' ? data.moduleLabel : undefined,
    viewKey: typeof data.viewKey === 'string' ? data.viewKey : undefined,
    viewLabel: typeof data.viewLabel === 'string' ? data.viewLabel : undefined,
    search: typeof data.search === 'string' ? data.search : undefined,
    tabId: typeof data.tabId === 'string' ? data.tabId : undefined,
    sessionId: typeof data.sessionId === 'string' ? data.sessionId : undefined,
    attributedTo: (data.attributedTo as CrmActivitySession['attributedTo']) || undefined,
    projectId: typeof data.projectId === 'string' ? data.projectId : undefined,
    leadId: typeof data.leadId === 'string' ? data.leadId : undefined,
    focused: typeof data.focused === 'boolean' ? data.focused : undefined,
    visible: typeof data.visible === 'boolean' ? data.visible : undefined,
  };
}

/** Direct Firestore write (no queue). Used by the retry pump. */
export async function saveCrmActivitySessionDirect(
  session: Omit<CrmActivitySession, 'id'>,
): Promise<string> {
  if (!session.userId || (session.minutes <= 0 && !(session.presenceSeconds && session.presenceSeconds > 0))) {
    return '';
  }
  const ref = await addDoc(collection(db, CRM_ACTIVITY_SESSIONS), toFirestoreCrmActivitySession(session));
  return ref.id;
}

/**
 * Persist a session. When `queueOnFailure` is true, failed writes are
 * queued in localStorage and retried later.
 */
export async function saveCrmActivitySession(
  session: Omit<CrmActivitySession, 'id'>,
  options?: { queueOnFailure?: boolean },
): Promise<string> {
  if (!session.userId || (session.minutes <= 0 && !(session.presenceSeconds && session.presenceSeconds > 0))) {
    return '';
  }
  try {
    const id = await saveCrmActivitySessionDirect(session);
    if (id) await bumpCrmTrackerHealth(session.userId, 'written');
    return id;
  } catch (err) {
    await bumpCrmTrackerHealth(session.userId, 'failed');
    if (options?.queueOnFailure) {
      enqueueCrmActivitySessionWrite(session);
      console.error('[CrmActivity] Session write failed; queued for retry:', err);
      return '';
    }
    throw err;
  }
}

export async function fetchCrmActivitySessionsInRange(options: {
  start: Date;
  end: Date;
  userId?: string;
}): Promise<CrmActivitySession[]> {
  const startTs = Timestamp.fromDate(options.start);
  const endTs = Timestamp.fromDate(options.end);
  const col = collection(db, CRM_ACTIVITY_SESSIONS);

  const q = options.userId
    ? query(
      col,
      where('userId', '==', options.userId),
      where('startedAt', '>=', startTs),
      where('startedAt', '<=', endTs),
    )
    : query(
      col,
      where('startedAt', '>=', startTs),
      where('startedAt', '<=', endTs),
    );

  const snap = await getDocs(q);
  return snap.docs
    .map((d) => fromFirestoreCrmActivitySession(d.id, d.data() as Record<string, unknown>))
    .sort((a, b) => b.startedAt.getTime() - a.startedAt.getTime());
}
